import React, { useMemo } from "react";
import * as THREE from "three";
import { Line, Text } from "@react-three/drei";

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function samePoint(a, b, eps = 0.001) {
  return (
    Math.abs(toNumber(a?.x) - toNumber(b?.x)) <= eps &&
    Math.abs(toNumber(a?.z) - toNumber(b?.z)) <= eps
  );
}

function normalize2D(x, z) {
  const len = Math.sqrt(x * x + z * z) || 1;
  return { x: x / len, z: z / len };
}

function getInwardVector(value) {
  const inward = String(value || "").toLowerCase();

  if (inward === "positive_x") return { x: 1, z: 0 };
  if (inward === "negative_x") return { x: -1, z: 0 };
  if (inward === "positive_z") return { x: 0, z: 1 };
  if (inward === "negative_z") return { x: 0, z: -1 };

  return { x: 0, z: 1 };
}

function lineIntersection2D(p1, d1, p2, d2) {
  const det = d1.x * d2.z - d1.z * d2.x;
  if (Math.abs(det) < 1e-9) return null;

  const dx = p2.x - p1.x;
  const dz = p2.z - p1.z;

  const t = (dx * d2.z - dz * d2.x) / det;

  return {
    x: p1.x + d1.x * t,
    z: p1.z + d1.z * t,
  };
}

function getWallData(wall) {
  const start = {
    x: toNumber(wall?.start?.x),
    z: toNumber(wall?.start?.z),
  };

  const end = {
    x: toNumber(wall?.end?.x),
    z: toNumber(wall?.end?.z),
  };

  const dx = end.x - start.x;
  const dz = end.z - start.z;

  const tangent = normalize2D(dx, dz);
  const length = Math.sqrt(dx * dx + dz * dz) || 1;
  const angle = Math.atan2(dz, dx);

  const height = toNumber(wall?.height, 2800);
  const thickness = toNumber(wall?.thickness, 80);

  // Podržavamo oba naziva: inward i inwardDirection
  const inward = getInwardVector(wall?.inwardDirection || wall?.inward);
  const outward = {
    x: -inward.x,
    z: -inward.z,
  };

  const centerInner = {
    x: (start.x + end.x) / 2,
    z: (start.z + end.z) / 2,
  };

  return {
    id: wall?.id,
    name: wall?.name,
    start,
    end,
    dx,
    dz,
    tangent,
    inward,
    outward,
    length,
    angle,
    height,
    thickness,
    centerInner,
  };
}

function getConnectedWallAtPoint(currentWall, walls, point) {
  return (walls || []).find((wall) => {
    if (String(wall.id) === String(currentWall.id)) return false;

    const data = getWallData(wall);

    return samePoint(data.start, point) || samePoint(data.end, point);
  });
}

function getOuterCornerPoint(currentWall, walls, at = "start") {
  const current = getWallData(currentWall);
  const corner = at === "start" ? current.start : current.end;

  const currentOuterBase = {
    x: corner.x + current.outward.x * current.thickness,
    z: corner.z + current.outward.z * current.thickness,
  };

  const connectedWall = getConnectedWallAtPoint(currentWall, walls, corner);

  if (!connectedWall) {
    return currentOuterBase;
  }

  const other = getWallData(connectedWall);

  const otherOuterBase = {
    x: corner.x + other.outward.x * other.thickness,
    z: corner.z + other.outward.z * other.thickness,
  };

  const hit = lineIntersection2D(
    currentOuterBase,
    current.tangent,
    otherOuterBase,
    other.tangent
  );

  return hit || currentOuterBase;
}

function buildWallGeometry(wall, walls) {
  const data = getWallData(wall);

  const innerStart = data.start;
  const innerEnd = data.end;

  const outerStart = getOuterCornerPoint(wall, walls, "start");
  const outerEnd = getOuterCornerPoint(wall, walls, "end");

  const shape = new THREE.Shape();

  // Shape je u XY ravni, koristimo x i -z, zatim rotiramo u XZ/Y prostor.
  shape.moveTo(innerStart.x, -innerStart.z);
  shape.lineTo(innerEnd.x, -innerEnd.z);
  shape.lineTo(outerEnd.x, -outerEnd.z);
  shape.lineTo(outerStart.x, -outerStart.z);
  shape.closePath();

  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth: data.height,
    bevelEnabled: false,
  });

  geometry.rotateX(-Math.PI / 2);
  geometry.computeVertexNormals();

  return geometry;
}

function RoomFloorCeiling({ room }) {
  const width = toNumber(room?.width, 3000);
  const depth = toNumber(room?.depth, 2200);
  const height = toNumber(room?.height, 2800);

  const centerX = width / 2;
  const centerZ = depth / 2;

  return (
    <group>
      {room?.showFloor !== false && (
        <mesh
          position={[centerX, -1, centerZ]}
          rotation={[-Math.PI / 2, 0, 0]}
          receiveShadow
        >
          <planeGeometry args={[width, depth]} />
          <meshStandardMaterial
            color="#e5e7eb"
            transparent
            opacity={0.25}
            side={THREE.DoubleSide}
            depthWrite={false}
          />
        </mesh>
      )}

      {room?.showCeiling && (
        <mesh
          position={[centerX, height, centerZ]}
          rotation={[-Math.PI / 2, 0, 0]}
        >
          <planeGeometry args={[width, depth]} />
          <meshStandardMaterial
            color="#f8fafc"
            transparent
            opacity={0.1}
            side={THREE.DoubleSide}
            depthWrite={false}
          />
        </mesh>
      )}
    </group>
  );
}

function WallMesh({
  wall,
  walls,
  selected,
  onSelect,
  toolMode,
  onWallFaceClick,
}) {
  const geometry = useMemo(() => buildWallGeometry(wall, walls), [wall, walls]);
  const edges = useMemo(() => new THREE.EdgesGeometry(geometry), [geometry]);

  return (
    <group
  onClick={(e) => {
    e.stopPropagation();

    if (toolMode === "align") {
      const hit = e.intersections?.[0];

      if (!hit?.point) return;

      const data = getWallData(wall);

      let normal = new THREE.Vector3(
        data.inward.x,
        0,
        data.inward.z
      ).normalize();

      if (hit.face?.normal && hit.object?.matrixWorld) {
        normal = hit.face.normal
          .clone()
          .transformDirection(hit.object.matrixWorld)
          .normalize();
      }

      const point = hit.point.clone();

      onWallFaceClick?.({
        type: "roomWall",
        wallId: wall.id,
        center: point,
        point,
        normal,
      });

      return;
    }

    onSelect?.(wall.id);
  }}
>
      <mesh geometry={geometry}>
        <meshStandardMaterial
          color={selected ? "#60a5fa" : "#cbd5e1"}
          transparent
          opacity={selected ? 0.32 : 0.2}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </mesh>

      <lineSegments geometry={edges}>
        <lineBasicMaterial color={selected ? "#2563eb" : "#64748b"} />
      </lineSegments>
    </group>
  );
}

function WallOffsetGuides({ wall, room }) {
  const data = getWallData(wall);

  const roomHeight = toNumber(room?.height || wall?.height, 2800);
  const baseFloorOffset = toNumber(room?.baseFloorOffset, 150);
  const wallCabinetOffset = toNumber(room?.wallCabinetOffset, 1500);
  const ceilingClearance = toNumber(room?.ceilingClearance, 50);
const maxCabinetTop = roomHeight - ceilingClearance;

  const guideOffsetFromWall = 10;

  // Guide linije treba da budu na unutrašnjoj strani zida,
  // malo pomerene ka prostoru da se vide preko zida.
  const baseStart = [
    data.start.x + data.inward.x * guideOffsetFromWall,
    baseFloorOffset,
    data.start.z + data.inward.z * guideOffsetFromWall,
  ];

  const baseEnd = [
    data.end.x + data.inward.x * guideOffsetFromWall,
    baseFloorOffset,
    data.end.z + data.inward.z * guideOffsetFromWall,
  ];

  const wallStart = [
    data.start.x + data.inward.x * guideOffsetFromWall,
    wallCabinetOffset,
    data.start.z + data.inward.z * guideOffsetFromWall,
  ];

  const wallEnd = [
    data.end.x + data.inward.x * guideOffsetFromWall,
    wallCabinetOffset,
    data.end.z + data.inward.z * guideOffsetFromWall,
  ];

  const heightStart = [
    data.start.x + data.inward.x * guideOffsetFromWall,
    roomHeight,
    data.start.z + data.inward.z * guideOffsetFromWall,
  ];

  const heightEnd = [
    data.end.x + data.inward.x * guideOffsetFromWall,
    roomHeight,
    data.end.z + data.inward.z * guideOffsetFromWall,
  ];

  const ceilingGuideStart = [
  data.start.x + data.inward.x * guideOffsetFromWall,
  maxCabinetTop,
  data.start.z + data.inward.z * guideOffsetFromWall,
];

const ceilingGuideEnd = [
  data.end.x + data.inward.x * guideOffsetFromWall,
  maxCabinetTop,
  data.end.z + data.inward.z * guideOffsetFromWall,
];

  const labelCenterX = data.centerInner.x + data.inward.x * 30;
  const labelCenterZ = data.centerInner.z + data.inward.z * 30;

  const labelRotationY = -data.angle;

  return (
    <group>
      <Line points={[baseStart, baseEnd]} color="#22c55e" lineWidth={1.5} />

      <Text
        position={[labelCenterX, baseFloorOffset + 45, labelCenterZ]}
        rotation={[0, labelRotationY, 0]}
        fontSize={42}
        color="#16a34a"
        anchorX="center"
        anchorY="middle"
      >
        {`Base ${baseFloorOffset} mm`}
      </Text>

      <Line points={[wallStart, wallEnd]} color="#f59e0b" lineWidth={1.5} />

      <Text
        position={[labelCenterX, wallCabinetOffset + 45, labelCenterZ]}
        rotation={[0, labelRotationY, 0]}
        fontSize={42}
        color="#d97706"
        anchorX="center"
        anchorY="middle"
      >
        {`Wall cabinets ${wallCabinetOffset} mm`}
      </Text>

      <Line points={[heightStart, heightEnd]} color="#94a3b8" lineWidth={1} />

      <Text
        position={[labelCenterX, roomHeight + 55, labelCenterZ]}
        rotation={[0, labelRotationY, 0]}
        fontSize={42}
        color="#64748b"
        anchorX="center"
        anchorY="middle"
      >
        {`${Math.round(data.length)} mm · H ${roomHeight} mm`}
      </Text>

      <Line
  points={[ceilingGuideStart, ceilingGuideEnd]}
  color="#ef4444"
  lineWidth={1.2}
/>

<Text
  position={[labelCenterX, maxCabinetTop + 45, labelCenterZ]}
  rotation={[0, labelRotationY, 0]}
  fontSize={42}
  color="#dc2626"
  anchorX="center"
  anchorY="middle"
>
  {`Max top ${maxCabinetTop} mm · gap ${ceilingClearance} mm`}
</Text>
    </group>
  );
}

function OpeningMarker({ opening, wall, selected, onSelect }) {
  if (!wall) return null;

  const data = getWallData(wall);

  const offset = toNumber(opening.offset, 0);
  const width = toNumber(opening.width, 800);
  const height = toNumber(opening.height, 2100);
  const sillHeight = toNumber(opening.sillHeight, 0);

  const centerAlongWall = offset + width / 2;

  const x =
    data.start.x +
    data.tangent.x * centerAlongWall +
    data.inward.x * 24;

  const z =
    data.start.z +
    data.tangent.z * centerAlongWall +
    data.inward.z * 24;

  const y = sillHeight + height / 2;

  const color =
    opening.type === "door"
      ? "#ef4444"
      : opening.type === "passage"
      ? "#a855f7"
      : "#f97316";

  return (
    <group
      position={[x, y, z]}
      rotation={[0, -data.angle, 0]}
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.(opening.id);
      }}
    >
      <mesh>
        <boxGeometry args={[width, height, selected ? 34 : 20]} />
        <meshStandardMaterial
          color={selected ? "#facc15" : color}
          transparent
          opacity={selected ? 0.75 : 0.5}
        />
      </mesh>

      <Text
        position={[0, height / 2 + 70, 14]}
        fontSize={42}
        color={selected ? "#ca8a04" : color}
        anchorX="center"
        anchorY="middle"
      >
        {`${opening.type} ${width}mm`}
      </Text>
    </group>
  );
}

function ServiceMarker({ service, wall, selected, onSelect }) {
  if (!wall) return null;

  const data = getWallData(wall);

  const offset = toNumber(service.offset, 0);
  const height = toNumber(service.height, 1000);

  const x =
    data.start.x +
    data.tangent.x * offset +
    data.inward.x * 38;

  const z =
    data.start.z +
    data.tangent.z * offset +
    data.inward.z * 38;

  const color =
    service.type === "water"
      ? "#2563eb"
      : service.type === "drain"
      ? "#334155"
      : service.type === "ventilation"
      ? "#22c55e"
      : service.type === "gas"
      ? "#f97316"
      : "#eab308";

  return (
    <group
      position={[x, height, z]}
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.(service.id);
      }}
    >
      <mesh>
        <sphereGeometry args={[selected ? 48 : 35, 16, 16]} />
        <meshStandardMaterial color={selected ? "#facc15" : color} />
      </mesh>

      <Text
        position={[0, 80, 0]}
        fontSize={38}
        color={selected ? "#ca8a04" : color}
        anchorX="center"
        anchorY="middle"
      >
        {service.label || service.type}
      </Text>
    </group>
  );
}

export default function RoomWallOverlay({
  room,
  selectedRoomObjectId,
  onSelectRoomObject,
  toolMode,
  onWallFaceClick,
}) {
  const walls = room?.walls || [];
  const openings = room?.openings || [];
  const services = room?.services || [];

  return (
    <group>
      <RoomFloorCeiling room={room} />

      {walls.map((wall) => (
        <React.Fragment key={wall.id}>
          <WallMesh
  wall={wall}
  walls={walls}
  selected={String(selectedRoomObjectId) === String(wall.id)}
  onSelect={onSelectRoomObject}
  toolMode={toolMode}
  onWallFaceClick={onWallFaceClick}
/>

          <WallOffsetGuides wall={wall} room={room} />
        </React.Fragment>
      ))}

      {openings.map((opening) => {
        const wall = walls.find((w) => String(w.id) === String(opening.wallId));

        return (
          <OpeningMarker
            key={opening.id}
            opening={opening}
            wall={wall}
            selected={String(selectedRoomObjectId) === String(opening.id)}
            onSelect={onSelectRoomObject}
          />
        );
      })}

      {services.map((service) => {
        const wall = walls.find((w) => String(w.id) === String(service.wallId));

        return (
          <ServiceMarker
            key={service.id}
            service={service}
            wall={wall}
            selected={String(selectedRoomObjectId) === String(service.id)}
            onSelect={onSelectRoomObject}
          />
        );
      })}
    </group>
  );
}